"use client";

import * as React from "react";
import { scaleSqrt } from "d3-scale";
import { VOCABULARY } from "../../../data/viz";
import { useSize } from "../../../hooks";
import VizFrame from "./VizFrame";

type Placed = {
  text: string;
  value: number;
  x: number;
  y: number;
  size: number;
  w: number;
  h: number;
};

/**
 * Spiral-packed word cloud.
 *
 * Deliberately the weakest encoding in this gallery, and included because it
 * is the right one for the question: term frequency across 284 documents has
 * no order worth preserving, and the honest goal is gist, not comparison.
 *
 * Placement walks an Archimedean spiral out from the centre and keeps the
 * first position with no bounding-box overlap. Layout is deterministic, so it
 * does not reshuffle on every render.
 */
function layout(
  terms: readonly { text: string; value: number }[],
  width: number,
  height: number,
): Placed[] {
  if (width < 40 || height < 40) return [];

  const max = terms[0]?.value ?? 1;
  const min = terms[terms.length - 1]?.value ?? 1;
  const size = scaleSqrt()
    .domain([min, max])
    .range([Math.max(10, width / 62), Math.max(26, width / 13)]);

  const placed: Placed[] = [];
  const cx = width / 2;
  const cy = height / 2;

  for (const t of terms) {
    const fs = size(t.value);
    // Cheap metrics: Inter averages a little over half the font size per glyph.
    const w = t.text.length * fs * 0.55 + 8;
    const h = fs * 1.16;

    let found = false;
    for (let step = 0; step < 2600 && !found; step++) {
      const angle = step * 0.32;
      const radius = 2.4 * angle;
      const x = cx + Math.cos(angle) * radius - w / 2;
      const y = cy + Math.sin(angle) * radius * 0.58 - h / 2;

      if (x < 2 || y < 2 || x + w > width - 2 || y + h > height - 2) continue;

      const clash = placed.some(
        (p) =>
          x < p.x + p.w + 3 &&
          x + w + 3 > p.x &&
          y < p.y + p.h + 2 &&
          y + h + 2 > p.y,
      );
      if (clash) continue;

      placed.push({ text: t.text, value: t.value, x, y, size: fs, w, h });
      found = true;
    }
  }
  return placed;
}

export default function WordCloud() {
  const [ref, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = React.useState<string | null>(null);

  const w = Math.max(size.width - 24, 240);
  const h = Math.min(Math.max(Math.round(w * 0.62), 260), 400);

  const placed = React.useMemo(() => layout(VOCABULARY, w, h), [w, h]);
  const max = VOCABULARY[0]?.value ?? 1;

  return (
    <VizFrame
      title="What the design docs talk about"
      meta="284 documents · 388k words"
    >
      <div ref={ref} className="p-3">
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label={`Word cloud of design documentation. Most frequent terms: ${VOCABULARY.slice(
            0,
            8,
          )
            .map((t) => t.text)
            .join(", ")}.`}
        >
          {placed.map((p) => {
            const weight = p.value / max;
            const dim = hover !== null && hover !== p.text;
            return (
              <text
                key={p.text}
                x={p.x}
                y={p.y + p.size}
                fontSize={p.size}
                className="font-medium"
                fill={weight > 0.45 ? "rgb(var(--accent))" : "rgb(var(--ink))"}
                opacity={dim ? 0.2 : 0.35 + weight * 0.65}
                onMouseEnter={() => setHover(p.text)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "crosshair", transition: "opacity .15s" }}
              >
                {p.text}
                <title>{`${p.text}: ${p.value.toLocaleString()} occurrences`}</title>
              </text>
            );
          })}
        </svg>
      </div>
    </VizFrame>
  );
}
